import {
  ArrowLeftRight,
  BriefcaseBusiness,
  CircleHelp,
  Home,
  Images,
  Landmark,
  Menu,
  Sparkles,
  WalletCards,
  X,
} from "lucide-react";
import { useState, type MouseEvent } from "react";

import { compactAddress } from "@web/features/terminal/presentation";
import { targetChain } from "@stonkland/protocol/chain";
import { routePaths, type RouteKey } from "@web/routes";
import type { WalletSessionStatus } from "@web/wallet/session";

type SiteHeaderProps = {
  account?: string;
  busy?: string;
  protocolLive: boolean;
  route: RouteKey;
  walletChainId?: number;
  walletConfigured: boolean;
  walletStatus: WalletSessionStatus;
  onWalletAction: () => void;
  onNavigate: (route: RouteKey) => void;
};

const desktopNavigation: Array<{
  href: string;
  label: string;
  route: RouteKey;
}> = [
  { href: routePaths.trade, label: "Trade", route: "trade" },
  { href: routePaths.portfolio, label: "Portfolio", route: "portfolio" },
  { href: routePaths.rewards, label: "Rewards", route: "rewards" },
  { href: routePaths.loans, label: "Borrow", route: "loans" },
  { href: routePaths.collection, label: "Collection", route: "collection" },
  { href: routePaths.protocol, label: "About", route: "protocol" },
];

const mobileNavigation = [
  { href: routePaths.home, icon: Home, label: "Home", route: "home" as const },
  {
    href: routePaths.trade,
    icon: ArrowLeftRight,
    label: "Trade",
    route: "trade" as const,
  },
  {
    href: routePaths.portfolio,
    icon: BriefcaseBusiness,
    label: "Portfolio",
    route: "portfolio" as const,
  },
  {
    href: routePaths.rewards,
    icon: Sparkles,
    label: "Rewards",
    route: "rewards" as const,
  },
];

const moreNavigation = [
  {
    href: routePaths.loans,
    icon: Landmark,
    label: "Borrow",
    route: "loans" as const,
  },
  {
    href: routePaths.collection,
    icon: Images,
    label: "Collection",
    route: "collection" as const,
  },
  {
    href: routePaths.protocol,
    icon: CircleHelp,
    label: "About Stonkland",
    route: "protocol" as const,
  },
];

export function SiteHeader({
  account,
  busy,
  protocolLive,
  route,
  walletChainId,
  walletConfigured,
  walletStatus,
  onWalletAction,
  onNavigate,
}: SiteHeaderProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const navigate =
    (destination: RouteKey) =>
    (event: MouseEvent<HTMLAnchorElement>) => {
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }
      event.preventDefault();
      setMoreOpen(false);
      onNavigate(destination);
    };
  const moreActive = moreNavigation.some((item) => item.route === route);

  return (
    <header className="site-header">
      <a
        className="brand"
        href={routePaths.home}
        aria-label="Stonkland home"
        onClick={navigate("home")}
      >
        <img
          className="brand-mark"
          src="/brand/stonkland-mark.svg"
          width={32}
          height={32}
          alt=""
        />
        <span>STONKLAND</span>
      </a>

      <nav className="desktop-navigation" aria-label="Main navigation">
        {desktopNavigation.map((item) => (
          <a
            key={item.href}
            href={item.href}
            className={route === item.route ? "nav-active" : ""}
            onClick={navigate(item.route)}
          >
            {item.label}
          </a>
        ))}
      </nav>

      <div className="wallet-zone">
        <span className={`network-pill ${protocolLive ? "" : "offline"}`}>
          <i />
          {protocolLive ? targetChain.name.toUpperCase() : "API OFFLINE"}
        </span>
        <button
          className="wallet-button"
          disabled={Boolean(busy)}
          onClick={onWalletAction}
          aria-label={
            account && walletChainId !== targetChain.id
              ? `Switch wallet to ${targetChain.name}`
              : account
                ? "Open wallet account"
                : "Connect wallet"
          }
        >
          <WalletCards size={16} />
          {!walletConfigured
            ? "APPKIT NOT CONFIGURED"
            : busy === "wallet" ||
                walletStatus === "connecting" ||
                walletStatus === "reconnecting"
            ? "CONNECTING"
            : account && walletChainId !== targetChain.id
              ? "SWITCH NETWORK"
              : account
                ? compactAddress(account)
                : "CONNECT WALLET"}
        </button>
      </div>

      {moreOpen && (
        <>
          <button
            className="mobile-nav-scrim"
            aria-label="Close navigation menu"
            onClick={() => setMoreOpen(false)}
            type="button"
          />
          <div className="mobile-more-menu" role="dialog" aria-label="More pages">
            <div className="mobile-more-head">
              <strong>More</strong>
              <button
                aria-label="Close navigation menu"
                onClick={() => setMoreOpen(false)}
                type="button"
              >
                <X size={18} />
              </button>
            </div>
            {moreNavigation.map(({ href, icon: Icon, label, route: itemRoute }) => (
              <a
                className={route === itemRoute ? "nav-active" : ""}
                href={href}
                key={href}
                onClick={navigate(itemRoute)}
              >
                <Icon size={18} />
                <span>{label}</span>
              </a>
            ))}
          </div>
        </>
      )}

      <nav className="mobile-bottom-nav" aria-label="Mobile navigation">
        {mobileNavigation.map(({ href, icon: Icon, label, route: itemRoute }) => (
          <a
            className={route === itemRoute ? "nav-active" : ""}
            href={href}
            key={href}
            onClick={navigate(itemRoute)}
          >
            <Icon size={20} />
            <span>{label}</span>
          </a>
        ))}
        <button
          className={moreActive || moreOpen ? "nav-active" : ""}
          aria-expanded={moreOpen}
          aria-label="More pages"
          onClick={() => setMoreOpen((open) => !open)}
          type="button"
        >
          <Menu size={20} />
          <span>More</span>
        </button>
      </nav>
    </header>
  );
}
